// LipsyncEngine — wrapper over the official WebAssembly build of
// git.vampi.tech/vampi/lipsync (vendored at client/public/lipsync/).
//
// The glue (lipsync.js) + binary (lipsync.wasm) live in public/ and are loaded
// on demand the first time a puppet needs them — so nothing is fetched unless a
// puppet is enabled. Feed mono audio (any sample rate); it resamples to 16 kHz
// and runs the streaming viseme predictor, returning one viseme id (0..11) per
// 10 ms frame.

const SAMPLE_RATE = 16000;
const FPS = 100; // one viseme per 10 ms

// Public path where lipsync.js + lipsync.wasm are served (Vue copies public/ to
// the site root; BASE_URL is the configured publicPath, default '/').
const PUBLIC_BASE = `${process.env.BASE_URL || '/'}lipsync/`;
// These assets have fixed filenames but are cached `immutable` by nginx. Bump
// this whenever the vendored lipsync build changes so clients refetch it.
const LIPSYNC_VERSION = '1.0.0';
const V = `?v=${LIPSYNC_VERSION}`;

// Linear-resample mono float samples to 16 kHz.
export function resampleTo16k(samples, srcRate) {
	if(srcRate === SAMPLE_RATE) return samples;
	const ratio = SAMPLE_RATE / srcRate;
	const outLen = Math.max(0, Math.round(samples.length * ratio));
	const out = new Float32Array(outLen);
	for(let i = 0; i < outLen; i++) {
		const srcPos = i / ratio;
		const i0 = Math.floor(srcPos);
		const i1 = Math.min(i0 + 1, samples.length - 1);
		const frac = srcPos - i0;
		out[i] = samples[i0] * (1 - frac) + samples[i1] * frac;
	}
	return out;
}

function loadScript(src) {
	return new Promise((resolve, reject) => {
		const s = document.createElement('script');
		s.src = src;
		s.async = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error(`Failed to load ${src}`));
		document.head.appendChild(s);
	});
}

let modulePromise = null;
function getModule() {
	return modulePromise ||= (async () => {
		if(!window.LipsyncModule) await loadScript(`${PUBLIC_BASE}lipsync.js${V}`);
		// This build exports ccall/_malloc/_free and the ls_* functions, but not
		// the HEAPF32 view. Instantiate the wasm ourselves so we can grab the
		// WebAssembly.Memory and build our own typed-array view to pass samples.
		const wasmBytes = await fetch(`${PUBLIC_BASE}lipsync.wasm${V}`)
			.then(r => { if(!r.ok) throw new Error(`lipsync.wasm ${r.status}`); return r.arrayBuffer(); });
		let memory = null;
		const mod = await window.LipsyncModule({
			locateFile: path => `${PUBLIC_BASE}${path}`,
			instantiateWasm: (imports, done) => {
				WebAssembly.instantiate(wasmBytes, imports).then(result => {
					for(const exp of Object.values(result.instance.exports)) {
						if(exp instanceof WebAssembly.Memory) memory = exp;
					}
					done(result.instance);
				});
				return {};
			},
		});
		mod.__memory = memory;
		mod.ccall('ls_init', null, [], []);
		return mod;
	})();
}

// The wasm holds a single stream, so serialize analyze() calls (alerts may
// preload in parallel) to avoid interleaving pushes on the shared state.
let analyzeChain = Promise.resolve();

export default class LipsyncEngine {
	// Kick off glue/wasm loading ahead of time (optional).
	static preload() { return getModule(); }

	// Analyze mono float samples → { visemes: Int8Array, fps: 100 }.
	// `visemes[t]` is the mouth shape for the interval [t/fps, (t+1)/fps) seconds.
	static analyze(samples, sampleRate = SAMPLE_RATE) {
		const run = () => LipsyncEngine._analyze(samples, sampleRate);
		const p = analyzeChain.then(run, run);
		analyzeChain = p.catch(() => {});
		return p;
	}

	static async _analyze(samples, sampleRate) {
		const audio = resampleTo16k(samples, sampleRate);
		const n = audio.length;
		if(n <= 0) return { visemes: new Int8Array(0), fps: FPS };

		const wasm = await getModule();
		wasm.ccall('ls_reset', null, [], []); // fresh stream for this utterance

		const visemes = [];
		const drainFrames = () => {
			const count = wasm.ccall('ls_frame_count', 'number', [], []);
			for(let i = 0; i < count; i++) {
				visemes.push(wasm.ccall('ls_viseme', 'number', ['number'], [i]));
			}
			if(count > 0) wasm.ccall('ls_clear_frames', null, [], []);
		};

		const ptr = wasm._malloc(n * Float32Array.BYTES_PER_ELEMENT);
		try {
			// Fresh view: _malloc may have grown (and detached) the heap buffer.
			new Float32Array(wasm.__memory.buffer).set(audio, ptr >> 2);
			wasm.ccall('ls_push', null, ['number', 'number'], [ptr, n]);
			drainFrames();
			wasm.ccall('ls_flush', null, [], []);
			drainFrames();
		} finally {
			wasm._free(ptr);
		}

		return { visemes: Int8Array.from(visemes), fps: FPS };
	}
}

// Sample a viseme timeline at a playback time (seconds). Returns 0 (Neutral)
// outside the analyzed range.
export function visemeAt(result, timeSec) {
	if(!result || !result.visemes.length) return 0;
	let idx = Math.floor(timeSec * result.fps);
	if(idx < 0) idx = 0;
	if(idx >= result.visemes.length) return 0;
	return result.visemes[idx];
}
